import { EquipmentSectionConfig } from './equipmentTypes';

/** Sentinel value for the "add new equipment type" option in category dropdowns. */
export const ADD_EQUIPMENT_TYPE_OPTION_VALUE = '__add_equipment_type__';

export const ADD_EQUIPMENT_TYPE_OPTION_LABEL = '+ Add new Equipment category';

export const ADD_INSPECTION_FORM_OPTION_LABEL = '+ Add new Inspection form';

export const ADD_DAILY_SERVICE_FORM_OPTION_LABEL = '+ Add new Daily Service form';

/** Placeholder category id while a new category name is deferred until equipment save (restored drafts). */
export const PENDING_NEW_CATEGORY_ID = '__pending_new_category__';

export const EQUIPMENT_ENGINE_FIELD_KEYS = [
    'engineManufacturer',
    'engineModelNumber',
    'engineSerialNumber',
] as const;

export const isEquipmentEngineFieldKey = (key: string): boolean =>
    (EQUIPMENT_ENGINE_FIELD_KEYS as readonly string[]).includes(key);

export const groupEquipmentOverviewFields = <T extends { key: string }>(
    fields: T[],
): Array<{ kind: 'default' | 'engine'; fields: T[] }> => {
    const groups: Array<{ kind: 'default' | 'engine'; fields: T[] }> = [];
    for (const field of fields) {
        const kind = isEquipmentEngineFieldKey(field.key) ? 'engine' : 'default';
        const last = groups[groups.length - 1];
        if (last?.kind === kind) {
            last.fields.push(field);
        } else {
            groups.push({ kind, fields: [field] });
        }
    }
    return groups;
};

export const getEquipmentCategoryEditPath = (categoryId: string) => `/equipment/category/edit/${categoryId}`;

/** Default report/log date range when navigating from equipment unit snapshots. */
export const DEFAULT_REPORT_LOOKBACK_DAYS = 30;

export const NEW_UNIT_PRIORITY_FIELDS = [
    'categoryId',
    'name',
    'description',
    'unitOfMeasure',
    'locationId',
    'dailyServiceLogFormId',
];

/** Not part of edit-mode formData / Save — updated only via photo upload flow. */
export const SINGLE_UNIT_EDIT_FORM_EXCLUDED_KEYS = ['image', 'category'] as const;

export const EQUIPMENT_SECTION_CONFIGS: EquipmentSectionConfig[] = [
    { key: 'asset-details', title: 'Asset Details', editable: true },
    { key: 'manufacturing', title: 'Manufacturing & Acquisition', editable: true },
    { key: 'utilization', title: 'Utilization', editable: true },
    { key: 'inspection-summary', title: 'Inspection Summary', editable: true },
    { key: 'daily-service-summary', title: 'Daily Service Summary', editable: true },
    { key: 'assignment-organization', title: 'Assignment & Organization', editable: true },
    { key: 'lifecycle-status', title: 'Lifecycle & Status', editable: true },
];

/**
 * Fields accepted by the Gen2 `SingleUnit` model for create/update.
 * Gen1-only UI keys (notes, archivedDate, …) must not be sent until restored in amplify/data.
 */
export const GEN2_SINGLE_UNIT_WRITABLE_FIELDS = [
    'name',
    'description',
    'image',
    'categoryId',
    'assignedTo',
    'serviceCenterId',
    'manufacturerName',
    'manufactureDate',
    'modelNumber',
    'engineManufacturer',
    'engineModelNumber',
    'engineSerialNumber',
    'serialNumber',
    'licenseNumber',
    'aquisitionDate',
    'aquisitionCost',
    'inputDate',
    'inputByPerson',
    'unitOfMeasure',
    'currentMachineHours',
    'currentUMCounterValue',
    'machineUMCounterAtAquisition',
    'machineHoursAtAquisition',
    'lastServiceHours',
    'lastInspectionDate',
    'lastDailyServiceDate',
    'annualProjectedUsage',
    'utilizationAdjustmentDays',
    'pmModuleEnrollment',
    'tiresModuleEnrollment',
    'axleConfiguration',
    'costTrackingModuleEnrollment',
    'downReason',
    'locationId',
    'dailyServiceLogFormId',
    'extraUnitInfo',
    'equipmentStatus',
    'putInServiceDate',
    'replacedUMCounterStatus',
    'replacedUMCounterValue',
    'replacedUMCounterHistory',
    'editHistory',
    'removedFromServiceDate',
    'removedFromServiceReason',
    'dispositionReason',
    'dispositionDate',
] as const;

export const GEN2_SINGLE_UNIT_FLOAT_FIELDS = [
    'aquisitionCost',
    'currentMachineHours',
    'currentUMCounterValue',
    'machineUMCounterAtAquisition',
    'machineHoursAtAquisition',
    'lastServiceHours',
    'annualProjectedUsage',
    // utilizationAdjustmentDays — omit from GraphQL until API deploy (local-only)
    'replacedUMCounterValue',
] as const;
